// Типизированные обёртки над /api/v1/*

import { API_BASE, request } from "./client";
import { getToken } from "./authStorage";
import { ApiError } from "./types";
import type {
  ActiveSession,
  AdminKpis,
  AdminSidebar,
  AnalyticsOverview,
  AppCreate,
  AppDetailOut,
  AppLocaleCreate,
  AppLocaleOut,
  AppLocaleUpdate,
  AppOut,
  DevAppOut,
  DevSettingsOut,
  DevStatsOut,
  EndpointStats,
  ErrorEntry,
  EventOut,
  GroupOut,
  KeyOut,
  LocalCacheDeleteResponse,
  LocalCachedPackage,
  LocalCreate,
  LocalOut,
  LocalPeerLink,
  LocalServiceOut,
  LocalStatusOut,
  LocalSyncHistoryItem,
  LocalSyncTriggerResponse,
  LocalsSummary,
  LoginRequest,
  LoginResponse,
  MeResponse,
  Page,
  PublishVersionRequest,
  RevokeRequest,
  RotateKeyRequest,
  SecurityOverview,
  SettingsOut,
  SettingsUpdate,
  TimeBucket,
  TokenCreateResponse,
  TokenOut,
  TokenRevokeResponse,
  UserCreate,
  UserOut,
  VersionOut,
} from "./types";

// ─── auth ──────────────────────────────────────────────────────────────────

export function login(body: LoginRequest): Promise<LoginResponse> {
  return request<LoginResponse>("/auth/login", {
    method: "POST",
    body,
    skipAuthRedirect: true,
  });
}

export function me(opts: { skipAuthRedirect?: boolean } = {}): Promise<MeResponse> {
  return request<MeResponse>("/auth/me", { skipAuthRedirect: opts.skipAuthRedirect });
}

export function logout(): Promise<void> {
  return request<void>("/auth/logout", { method: "POST", skipAuthRedirect: true });
}

export function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  return request<void>("/auth/change-password", {
    method: "POST",
    body: { current_password: currentPassword, new_password: newPassword },
  });
}

// ─── apps ──────────────────────────────────────────────────────────────────

export type ListAppsParams = {
  q?: string;
  status?: string;
  developer?: string;
  page?: number;
  page_size?: number;
};

export function listApps(params: ListAppsParams = {}): Promise<Page<AppOut>> {
  return request<Page<AppOut>>("/apps", { query: params });
}

export function createApp(body: AppCreate): Promise<AppOut> {
  return request<AppOut>("/apps", { method: "POST", body });
}

export function getApp(pkg: string): Promise<AppDetailOut> {
  return request<AppDetailOut>(`/apps/${encodeURIComponent(pkg)}`);
}

export function listVersions(pkg: string): Promise<VersionOut[]> {
  return request<VersionOut[]>(`/apps/${encodeURIComponent(pkg)}/versions`);
}

export function publishVersion(pkg: string, body: PublishVersionRequest): Promise<VersionOut> {
  return request<VersionOut>(`/apps/${encodeURIComponent(pkg)}/versions`, {
    method: "POST",
    body,
  });
}

export function revokeVersion(
  pkg: string,
  version: string,
  body: RevokeRequest = {},
): Promise<VersionOut> {
  return request<VersionOut>(
    `/apps/${encodeURIComponent(pkg)}/versions/${encodeURIComponent(version)}/revoke`,
    { method: "POST", body },
  );
}

export function createAppLocale(pkg: string, body: AppLocaleCreate): Promise<AppLocaleOut> {
  return request<AppLocaleOut>(`/apps/${encodeURIComponent(pkg)}/locales`, {
    method: "POST",
    body,
  });
}

export function updateAppLocale(
  pkg: string,
  code: string,
  body: AppLocaleUpdate,
): Promise<AppLocaleOut> {
  return request<AppLocaleOut>(
    `/apps/${encodeURIComponent(pkg)}/locales/${encodeURIComponent(code)}`,
    { method: "PUT", body },
  );
}

export function deleteAppLocale(pkg: string, code: string): Promise<void> {
  return request<void>(
    `/apps/${encodeURIComponent(pkg)}/locales/${encodeURIComponent(code)}`,
    { method: "DELETE" },
  );
}

// ─── users / groups ────────────────────────────────────────────────────────

export function listUsers(group?: string | null): Promise<UserOut[]> {
  return request<UserOut[]>("/users", { query: { group: group ?? undefined } });
}

export function createUser(body: UserCreate): Promise<UserOut> {
  return request<UserOut>("/users", { method: "POST", body });
}

export function listGroups(): Promise<GroupOut[]> {
  return request<GroupOut[]>("/groups");
}

export type GroupCreate = {
  code: string;
  name: string;
  letters?: string;
  description?: string | null;
};

export function createGroup(body: GroupCreate): Promise<GroupOut> {
  return request<GroupOut>("/groups", { method: "POST", body });
}

export function renameGroup(code: string, name: string): Promise<GroupOut> {
  return request<GroupOut>(`/groups/${encodeURIComponent(code)}`, {
    method: "PATCH",
    body: { name },
  });
}

export function addUserToGroup(groupCode: string, login: string): Promise<UserOut> {
  return request<UserOut>(
    `/groups/${encodeURIComponent(groupCode)}/users/${encodeURIComponent(login)}`,
    { method: "POST" },
  );
}

export function removeUserFromGroup(groupCode: string, login: string): Promise<void> {
  return request<void>(
    `/groups/${encodeURIComponent(groupCode)}/users/${encodeURIComponent(login)}`,
    { method: "DELETE" },
  );
}

export type GroupPermissions = {
  group_code: string;
  sections: Record<string, boolean>;
};

export function getGroupPermissions(code: string): Promise<GroupPermissions> {
  return request<GroupPermissions>(`/groups/${encodeURIComponent(code)}/permissions`);
}

export function updateGroupPermissions(
  code: string,
  sections: Record<string, boolean>,
): Promise<GroupPermissions> {
  return request<GroupPermissions>(`/groups/${encodeURIComponent(code)}/permissions`, {
    method: "PUT",
    body: { sections },
  });
}

// ─── locals ────────────────────────────────────────────────────────────────

export function listLocals(): Promise<LocalOut[]> {
  return request<LocalOut[]>("/locals");
}

export function createLocal(body: LocalCreate): Promise<LocalOut> {
  return request<LocalOut>("/locals", { method: "POST", body });
}

export function localsSummary(): Promise<LocalsSummary> {
  return request<LocalsSummary>("/locals/summary");
}

// ─── events ────────────────────────────────────────────────────────────────

export type ListEventsParams = {
  q?: string;
  type?: string;
  page?: number;
  page_size?: number;
};

export function listEvents(params: ListEventsParams = {}): Promise<Page<EventOut>> {
  return request<Page<EventOut>>("/events", { query: params });
}

export function recentEvents(limit = 6): Promise<EventOut[]> {
  return request<EventOut[]>("/events/recent", { query: { limit } });
}

// ─── keys ──────────────────────────────────────────────────────────────────

export function listKeys(): Promise<KeyOut[]> {
  return request<KeyOut[]>("/keys");
}

export function rotateKey(body: RotateKeyRequest): Promise<KeyOut> {
  return request<KeyOut>("/keys/rotate", { method: "POST", body });
}

// ─── settings ──────────────────────────────────────────────────────────────

export function getSettings(): Promise<SettingsOut> {
  return request<SettingsOut>("/settings");
}

export function updateSettings(body: SettingsUpdate): Promise<SettingsOut> {
  return request<SettingsOut>("/settings", { method: "PUT", body });
}

// ─── admin (KPI dashboard / sidebar) ───────────────────────────────────────
// Бэкенд: astramarket-backend/src/astramarket/api/v1/admin/router.py

export function getAdminKpis(): Promise<AdminKpis> {
  return request<AdminKpis>("/admin/kpis");
}

export function getAdminSidebar(): Promise<AdminSidebar> {
  return request<AdminSidebar>("/admin/sidebar");
}

// ─── analytics / security ──────────────────────────────────────────────────

export function getAnalyticsOverview(): Promise<AnalyticsOverview> {
  return request<AnalyticsOverview>("/analytics/overview");
}

export function getEndpointStats(limit = 20): Promise<EndpointStats[]> {
  return request<EndpointStats[]>("/analytics/endpoints", { query: { limit } });
}

export function getTimeseries(buckets = 24): Promise<TimeBucket[]> {
  return request<TimeBucket[]>("/analytics/timeseries", { query: { buckets } });
}

export function getRecentErrors(limit = 50): Promise<ErrorEntry[]> {
  return request<ErrorEntry[]>("/analytics/errors", { query: { limit } });
}

export function getSecurityOverview(): Promise<SecurityOverview> {
  return request<SecurityOverview>("/analytics/security");
}

export function getActiveSessions(): Promise<ActiveSession[]> {
  return request<ActiveSession[]>("/analytics/sessions");
}

// ─── local node (текущий локал) ────────────────────────────────────────────
// Бэкенд: astramarket-backend/src/astramarket/api/v1/local/router.py

export function getLocalStatus(): Promise<LocalStatusOut> {
  return request<LocalStatusOut>("/local/status");
}

export function getLocalSyncHistory(): Promise<LocalSyncHistoryItem[]> {
  return request<LocalSyncHistoryItem[]>("/local/sync/history");
}

export function triggerLocalSync(): Promise<LocalSyncTriggerResponse> {
  return request<LocalSyncTriggerResponse>("/local/sync/now", { method: "POST" });
}

export function getLocalCache(): Promise<LocalCachedPackage[]> {
  return request<LocalCachedPackage[]>("/local/cache");
}

export function purgeLocalCache(pkg: string): Promise<LocalCacheDeleteResponse> {
  return request<LocalCacheDeleteResponse>(
    `/local/cache/${encodeURIComponent(pkg)}`,
    { method: "DELETE" },
  );
}

export function getLocalServices(): Promise<LocalServiceOut[]> {
  return request<LocalServiceOut[]>("/local/services");
}

export function getLocalNet(): Promise<LocalPeerLink[]> {
  return request<LocalPeerLink[]>("/local/net");
}

// ─── dev (кабинет разработчика) ────────────────────────────────────────────
// Бэкенд: astramarket-backend/src/astramarket/api/v1/dev/router.py

export function getMyApps(): Promise<DevAppOut[]> {
  return request<DevAppOut[]>("/dev/my-apps");
}

export function getDevSettings(): Promise<DevSettingsOut> {
  return request<DevSettingsOut>("/dev/settings");
}

export function updateDevSettings(body: DevSettingsOut): Promise<DevSettingsOut> {
  return request<DevSettingsOut>("/dev/settings", { method: "PUT", body });
}

export function getDevStats(pkg: string): Promise<DevStatsOut> {
  return request<DevStatsOut>("/dev/stats", { query: { pkg } });
}

export function getDevTokens(): Promise<TokenOut[]> {
  return request<TokenOut[]>("/dev/tokens");
}

export function createDevToken(name: string, scopes: string[] = []): Promise<TokenCreateResponse> {
  return request<TokenCreateResponse>("/dev/tokens", {
    method: "POST",
    body: { name, scopes },
  });
}

export function revokeDevToken(id: number): Promise<TokenRevokeResponse> {
  return request<TokenRevokeResponse>(`/dev/tokens/${id}`, { method: "DELETE" });
}

// Multipart-загрузка APK с прогрессом через XHR.
// Бэкенд: POST /apps/{pkg}/versions/upload (поле формы apk; changelog — best-effort,
// сейчас игнорируется сервером, но передаём — пригодится, когда добавят парсинг).
export function uploadApk(
  pkg: string,
  file: File,
  changelog?: string,
  onProgress?: (loaded: number, total: number) => void,
): Promise<VersionOut> {
  return new Promise<VersionOut>((resolve, reject) => {
    const url = `${API_BASE.replace(/\/+$/, "")}/apps/${encodeURIComponent(pkg)}/versions/upload`;
    const form = new FormData();
    form.append("apk", file, file.name);
    if (changelog && changelog.trim()) form.append("changelog", changelog);

    const xhr = new XMLHttpRequest();
    xhr.open("POST", url);
    xhr.responseType = "json";
    const token = getToken();
    if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    xhr.setRequestHeader("Accept", "application/json");

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(e.loaded, e.total);
    };

    xhr.onerror = () => {
      reject(new ApiError(0, "network_error", "Сеть недоступна"));
    };
    xhr.onabort = () => {
      reject(new ApiError(0, "aborted", "Загрузка отменена"));
    };
    xhr.onload = () => {
      const status = xhr.status;
      const payload: unknown = xhr.response;
      if (status >= 200 && status < 300) {
        resolve(payload as VersionOut);
        return;
      }
      let code = "http_error";
      let message = `HTTP ${status}`;
      let details: unknown = null;
      if (payload && typeof payload === "object") {
        const p = payload as { code?: string; message?: string; detail?: unknown; details?: unknown };
        if (typeof p.code === "string") code = p.code;
        if (typeof p.message === "string") message = p.message;
        else if (typeof p.detail === "string") message = p.detail;
        details = p.details ?? p.detail ?? null;
      }
      reject(new ApiError(status, code, message, details));
    };

    xhr.send(form);
  });
}
